import * as React from "react";
import { Badge as RizzBadge, Button as RizzButton } from "rizzui";
import cn from "../utils/class-names";

/**
 * The two template primitives the domain components are built on (`017` T042–T053).
 *
 * `packages/ui`'s rule-bearing components sat on shadcn/Radix primitives. The rebuilds sit on
 * `rizzui`'s — but with the *same prop surface*, deliberately, because the alternative was
 * rewriting thirteen components' call sites for no gain and thirteen chances to change a rule by
 * accident. The rule lives in the component above; this file is only what it renders through.
 *
 * ## The variant vocabulary is kept, not translated
 *
 * `default | secondary | destructive | outline | ghost` are the names the domain components use to
 * mean something — `destructive` is what a refusal looks like, not a colour choice. Those names
 * stay; what changes is the classes they resolve to, which now come from the template's own token
 * families (`red`, `green`, `orange`, `primary`, `gray`) instead of shadcn's.
 */

const BADGE_VARIANT = {
  default: "border border-transparent bg-primary text-primary-foreground",
  // Tinted, with a hairline inset edge in the same hue — never solid, never a grey outline
  // (2026-09-28, the Marketplace admin portal's flags).
  secondary: "border border-transparent bg-secondary-lighter/60 text-secondary-dark ring-secondary/25",
  destructive: "border border-transparent bg-red-lighter/50 text-red-dark ring-red/25",
  outline: "border border-transparent bg-gray-100 text-gray-700 ring-gray-300/70",
  ghost: "border border-transparent bg-transparent text-foreground",
  link: "border-none bg-transparent p-0 text-primary underline-offset-4",
} as const;

export type BadgeVariant = keyof typeof BADGE_VARIANT;

/**
 * Rendered as a `<span>` rather than through `rizzui`'s own `Badge`, which fixes its own
 * background and padding and cannot carry an icon beside a word. The domain badges must show a
 * mark, a word and a colour — three signals, because a dense table of statuses cannot be read by
 * hue alone, and prints monochrome (`006` R3). The class string is the template's badge shape.
 */
export function Badge({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"span"> & { variant?: BadgeVariant }) {
  return (
    <span
      data-slot="badge"
      data-variant={variant}
      className={cn(
        // The Marketplace admin portal's flag: a tinted pill in the body face (Inter), 12px medium,
        // no outline (2026-09-28). Was Lexend with wide tracking, which read as a heading.
        "inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border-transparent px-2.5 py-0.5 font-inter text-xs font-medium ring-1 ring-inset [&_svg]:pointer-events-none [&_svg]:size-3",
        BADGE_VARIANT[variant],
        className,
      )}
      {...props}
    />
  );
}

/** Kept so `rizzui`'s own `Badge` is still reachable where a plain dot or pill is wanted. */
export { RizzBadge };

const BUTTON_VARIANT = {
  default: "solid",
  secondary: "flat",
  outline: "outline",
  ghost: "text",
  destructive: "solid",
  link: "text",
} as const;

export type ButtonVariant = keyof typeof BUTTON_VARIANT;

/**
 * `rizzui`'s `Button` with the domain components' variant names in front of it. `destructive` maps
 * to a solid button in the template's `danger` colour: an irreversible action's confirm control
 * must not look like every other button on the screen (constitution VI).
 */
export function Button({
  variant = "default",
  className,
  ...props
}: Omit<React.ComponentProps<typeof RizzButton>, "variant" | "color"> & { variant?: ButtonVariant }) {
  return (
    <RizzButton
      variant={BUTTON_VARIANT[variant]}
      color={variant === "destructive" ? "danger" : "primary"}
      className={className}
      {...props}
    />
  );
}

/**
 * `rizzui`'s own `Input` container at its default `outline`/`md`/`rounded-md` settings, with its
 * JS-driven `is-hover`/`is-focus` classes swapped for the `:hover`/`:focus` they stand for. One
 * string for `Input` and `SelectField` alike, so a native control sits at exactly the height and
 * border of the rizzui `Textarea`/`Password`/`Input` beside it in the same form.
 */
export const controlClass =
  "h-10 w-full rounded-md border border-muted bg-transparent px-3.5 py-2 text-sm text-foreground shadow-none ring-[0.6px] ring-muted transition duration-200 placeholder:text-muted-foreground placeholder:opacity-60 hover:border-primary focus:border-primary focus:outline-none focus:ring-[0.8px] focus:ring-primary aria-invalid:border-red aria-invalid:ring-red disabled:cursor-not-allowed disabled:border-muted disabled:bg-muted/70";

/**
 * A bare `<input>`, restyled onto the template's tokens. Deliberately not `rizzui`'s `Input`, which
 * owns its own label, error and helper-text slots — `Field` already owns those, and stacking the
 * two would give every field two labels and two error lines.
 */
export function Input({ className, ...props }: React.ComponentProps<"input">) {
  return <input data-slot="input" className={cn(controlClass, className)} {...props} />;
}
